import { useEffect, useMemo, useRef, useState } from "react";
import type { Route } from "../router";
import { navigate, Link } from "../router";
import { useLang } from "../context/LangContext";
import { getGuides, getArticle, getArticleTitle } from "../content/docs";
import { searchContent, type SearchHit } from "../content/search";
import { renderMarkdown } from "../services/markdown";
import type { Lang } from "../i18n";

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

function stripTicks(s: string): string {
  return s.replace(/`/g, "");
}

function ArticleView({ slug, lang }: { slug: string; lang: Lang }) {
  const { t } = useLang();
  const article = getArticle(lang, slug);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  if (!article) {
    return (
      <div className="notice notice-err">
        {t.docsNotFound.lead}
        <Link to="/docs" className="muted-link">
          {t.docsNotFound.link}
        </Link>
        .
      </div>
    );
  }

  return (
    <article className="doc-article">
      <div className="doc-breadcrumb muted">
        <Link to="/docs" className="muted-link">
          {t.docsBreadcrumbRoot}
        </Link>{" "}
        / {article.guideTitle}
        {article.sectionTitle ? ` / ${article.sectionTitle}` : ""}
      </div>
      <h1 className="doc-title">{article.title}</h1>

      <div className="doc-meta">
        {article.who && (
          <span className="doc-meta-item">
            <span className="doc-meta-label">{t.metaWho}</span> {stripTicks(article.who)}
          </span>
        )}
        {article.where && (
          <span className="doc-meta-item">
            <span className="doc-meta-label">{t.metaWhere}</span> {stripTicks(article.where)}
          </span>
        )}
        {article.requires && (
          <span className="doc-meta-item">
            <span className="doc-meta-label">{t.metaRequires}</span> {stripTicks(article.requires)}
          </span>
        )}
      </div>

      <div
        className="doc-body"
        dangerouslySetInnerHTML={{ __html: renderMarkdown(article.body) }}
      />
    </article>
  );
}

function SearchResults({ hits, query }: { hits: SearchHit[]; query: string }) {
  const { t } = useLang();
  if (hits.length === 0) {
    return <div className="center-pad muted">{t.docsNoResults(query)}</div>;
  }
  return (
    <div className="doc-results">
      <p className="muted doc-results-count">{t.docsResultsCount(hits.length, query)}</p>
      {hits.map((h) => (
        <button
          key={h.route}
          className="doc-result"
          onClick={() => navigate(h.route)}
        >
          <div className="doc-result-head">
            <span className="doc-result-title">{h.title}</span>
            <span className={`badge${h.type === "faq" ? " badge-accent" : ""}`}>
              {h.context}
            </span>
          </div>
          <p className="doc-result-snippet muted">{h.snippet}</p>
        </button>
      ))}
    </div>
  );
}

export function DocsPage({ route }: { route: Route }) {
  const { lang, t } = useLang();
  const [query, setQuery] = useState("");
  const debounced = useDebounced(query.trim(), 200);
  const guides = getGuides(lang);

  // Everything after "docs" is the (possibly multi-segment) slug.
  const slug = route.segments.length > 1 ? route.segments.slice(1).join("/") : null;

  const hits = useMemo(
    () => (debounced.length >= 2 ? searchContent(lang, debounced) : null),
    [debounced, lang]
  );
  const searching = hits !== null;

  const searchRef = useRef<HTMLInputElement>(null);

  return (
    <div className="page docs">
      <div className="page-head">
        <h1>{t.docsTitle}</h1>
        <p className="muted">{t.docsSubtitle}</p>
      </div>

      <div className="docs-layout">
        <aside className="docs-sidebar">
          <div className="docs-search">
            <input
              ref={searchRef}
              type="search"
              placeholder={t.docsSearchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label={t.docsSearchAria}
            />
          </div>

          {guides.map((g) => (
            <div key={g.key} className="docs-nav-guide">
              <h4 className="docs-nav-guide-title">{g.title}</h4>
              {g.sections.map((s) => (
                <div key={s.key || "_"} className="docs-nav-group">
                  {s.title && <div className="docs-nav-section">{s.title}</div>}
                  <ul>
                    {s.slugs.map((sl) => (
                      <li key={sl}>
                        <a
                          href={`#/docs/${sl}`}
                          className={`docs-nav-link${slug === sl ? " active" : ""}`}
                        >
                          {getArticleTitle(lang, sl)}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ))}
        </aside>

        <section className="docs-content">
          {searching ? (
            <SearchResults hits={hits!} query={debounced} />
          ) : slug ? (
            <ArticleView slug={slug} lang={lang} />
          ) : (
            <DocsIndex />
          )}
        </section>
      </div>
    </div>
  );
}

function DocsIndex() {
  const { lang, t } = useLang();
  const guides = getGuides(lang);
  return (
    <div className="docs-index">
      <p className="muted">{t.docsIndexLead}</p>
      <div className="docs-index-grid">
        {guides.map((g) => {
          const first = g.sections[0]?.slugs[0];
          const count = g.sections.reduce((n, s) => n + s.slugs.length, 0);
          return (
            <button
              key={g.key}
              className="docs-index-card"
              onClick={() => first && navigate(`/docs/${first}`)}
            >
              <span className="docs-index-title">{g.title}</span>
              <span className="muted docs-index-summary">
                {t.docsArticlesCount(count)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
